import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { REFUND_POLICY_SUMMARY } from '../../constants/legal';
import {
  PLAN_MONTHLY_CREDITS,
  TRIAL_SIGNUP_CREDITS,
  monthlyCreditsLine,
  signupCreditsLine,
  planTermTotalLine,
} from '../../constants/plans';
import { MAX_LENGTHS } from '../../utils/inputValidation';
import './PricingPage.css';
import Footer from './Footer';
// 전자상거래법 제10조 -- the business disclosure has to be on the page that
// QUOTES THE PRICES, and this one had none of it: no 상호명, no 대표자명, no
// 사업자등록번호, no 주소, no 연락처. AppLayout early-returns `<>{children}</>`
// for '/' and '/pricing' (App.js:67) on the grounds that they 'have their own
// structure', so the app footer never reaches here -- and unlike '/', this page
// never rendered a footer of its own to compensate.
//
// It is not only a public page either: UserManagement.js:426 links AUTHENTICATED
// org admins straight here (target=_blank) from the seat-limit notice, so a
// logged-in customer following 'Upgrade subscription' lands on the one screen
// in the product that discloses nothing.
//
// This is the LANDING footer, the same component '/' uses, because the page is
// public and chrome-free. It already renders the full block from BUSINESS_INFO.

const PricingPage = () => {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';

  // ⚠ REBUILT 2026-09-03 — Refund-process-refactor-9-2.md T0.1a / §6.1.
  //
  // What was here was not stale marketing copy, it was WRONG PRICES on a
  // public, routed page (/pricing). It advertised:
  //   • a "Pay-as-you-go" credit-pack tier at 50/₩4,900, 200/₩16,800,
  //     500/₩38,000 — SKUs that have never existed in the server catalogue
  //     at any price, and packs are now withdrawn from sale entirely;
  //   • "Pro 얼리버드 ₩10,000/월, 정가 ₩39,000" against a real Pro price of
  //     ₩499,000/월 — off by a factor of fifty;
  //   • an early-bird deadline of 2025-06-30 and an Enterprise launch of
  //     "2025년 Q1", both more than a year in the past.
  //
  // A displayed price is an offer. Charging ₩499,000 against a published
  // ₩10,000 is the kind of discrepancy that ends a PG merchant review, and it
  // sat one click from a checkout that now takes real cards.
  //
  // Numbers below mirror backend/app/services/product_catalogue.py — Starter
  // ₩2,388,000/12mo (₩199,000/mo, 200 analysis reports/mo) and Pro
  // ₩5,988,000/12mo (₩499,000/mo, 600 analysis reports/mo). If they drift
  // again, the catalogue wins.
  //
  // 2026-09-28 -- the ALLOWANCE figures are no longer typed here. They were
  // literals inside the feature strings, and the '/' pricing section was about
  // to grow a second pair of literals for the same fact. Both surfaces now read
  // constants/plans.js, which backend/tests/test_plan_credits_match_frontend.py
  // pins to the catalogue above. The PRICES stay hand-written here on purpose:
  // this page quotes the ANNUAL total while '/' teases the monthly equivalent, a
  // deliberate difference (see A2 below) that a shared constant would flatten.
  //
  // 2026-10-03 (Portone-KGinisys-Pricing-fix-10-2.md T1.3) -- the Trial line,
  // the monthly lines and the 12-month totals all come from the plans.js
  // helpers, worded as a service with a fixed quantity (분석 리포트 N건 제공).
  // KG이니시스 reviews the stored-value wording this page used to carry as
  // 환금성 (cash-equivalent); see that plan §3 for the terminology standard.
  const plans = [
    {
      icon: '🆓',
      name: isKo ? 'Trial (무료 체험)' : 'Trial (free)',
      price: isKo ? '무료' : 'Free',
      // The shared signup line, which says 1회 / one time: a trial org is seeded
      // once at signup and never again (see TRIAL_SIGNUP_CREDITS).
      period: signupCreditsLine(TRIAL_SIGNUP_CREDITS, isKo),
      description: isKo
        ? '규제 검증을 처음 경험해보는 브랜드와 개인에게 적합합니다.'
        : 'For brands and individuals trying regulatory validation for the first time.',
      features: [
        { icon: '✓', text: isKo ? '한국 규제 검증' : 'Korean regulatory validation' },
        { icon: '✓', text: isKo ? '미국 FDA 규제 검증' : 'US FDA regulatory validation' },
        { icon: '✓', text: isKo ? 'AI 대체 표현 제안' : 'AI-suggested alternative wording' },
        { icon: '✓', text: isKo ? 'CSV 내보내기' : 'CSV export' },
      ],
      cta: isKo ? '무료로 시작하기' : 'Start free',
      ctaClass: 'btn-outline',
      ctaLink: '/login?signup=true',
    },
    {
      icon: '🚀',
      name: isKo ? '스타터 연간플랜' : 'Starter Yearly Plan',
      // A2 — the ANNUAL total is the price; the monthly is the breakdown. These
      // are one prepaid 12-month purchase (product_catalogue.plan_starter_12m),
      // never a monthly subscription, so quoting ₩199,000 as "the price" both
      // understated the commitment and invited the /월 suffix that used to be
      // appended here automatically.
      price: '₩2,388,000',
      priceSuffix: isKo ? '/ 년' : '/ yr',
      // D-2 (2026-10-03): "12개월 선불" became "12개월 이용료 일괄 결제" -- PortOne
      // lists 선불 서비스 as a high-risk category of its own. English matches
      // the '/' cards and checkout: "billed annually". Never "일시불".
      period: isKo ? '₩199,000 / 월 환산 · 12개월 이용료 일괄 결제' : '₩199,000 / mo equivalent · billed annually',
      description: isKo
        ? '1~3인 팀과 소규모 브랜드에 적합합니다.'
        : 'For teams of 1-3 and small brands.',
      features: [
        { icon: '✓', text: monthlyCreditsLine(PLAN_MONTHLY_CREDITS.starter, isKo) },
        // Always directly after the monthly line: the total is its consequence,
        // not a pool (planTermTotalLine).
        { icon: '✓', text: planTermTotalLine(PLAN_MONTHLY_CREDITS.starter, isKo) },
        { icon: '✓', text: isKo ? 'Trial 플랜의 모든 기능' : 'Everything in Trial' },
        { icon: '✓', text: isKo ? '검증 이력 관리' : 'Validation history' },
        { icon: '✓', text: isKo ? '규제 가이드 업데이트' : 'Regulatory guide updates' },
      ],
      cta: isKo ? '시작하기' : 'Get started',
      ctaClass: 'btn-outline',
      ctaLink: '/login?signup=true&tier=starter',
    },
    {
      icon: '🔥',
      name: isKo ? '프로 연간플랜' : 'Pro Yearly Plan',
      price: '₩5,988,000',
      priceSuffix: isKo ? '/ 년' : '/ yr',
      period: isKo ? '₩499,000 / 월 환산 · 12개월 이용료 일괄 결제' : '₩499,000 / mo equivalent · billed annually',
      description: isKo
        ? '정기적으로 여러 제품을 검증하는 브랜드와 대행사에 최적화된 플랜입니다.'
        : 'Built for brands and agencies validating multiple products on a regular cycle.',
      features: [
        { icon: '✓', text: monthlyCreditsLine(PLAN_MONTHLY_CREDITS.pro, isKo) },
        { icon: '✓', text: planTermTotalLine(PLAN_MONTHLY_CREDITS.pro, isKo) },
        { icon: '✓', text: isKo ? 'Starter 플랜의 모든 기능' : 'Everything in Starter' },
        { icon: '✓', text: isKo ? '감사 리포트 다운로드' : 'Audit report download' },
        { icon: '✓', text: isKo ? '팀 협업' : 'Team collaboration' },
      ],
      cta: isKo ? '시작하기' : 'Get started',
      ctaClass: 'btn-primary',
      ctaLink: '/login?signup=true&tier=pro',
      popular: true,
      badge: isKo ? '가장 인기' : 'Most popular',
    },
    {
      icon: '🏢',
      name: 'Enterprise',
      price: isKo ? '문의' : 'Contact us',
      period: isKo ? '맞춤형 견적' : 'Custom quote',
      description: isKo
        ? '대규모 브랜드와 OEM/ODM 파트너를 위한 맞춤형 솔루션입니다.'
        : 'A custom solution for large brands and OEM/ODM partners.',
      features: [
        { icon: '✓', text: isKo ? '무제한 검증 및 조직 운영' : 'Unlimited validation and team usage' },
        { icon: '✓', text: isKo ? 'OEM/ODM 전용 검토 프로세스' : 'OEM/ODM review workflow' },
        { icon: '✓', text: isKo ? '전담 컨설턴트 배정' : 'Dedicated consultant' },
        { icon: '✓', text: isKo ? 'API 액세스' : 'API access' },
      ],
      cta: isKo ? '영업팀 문의' : 'Contact sales',
      ctaClass: 'btn-outline',
      ctaLink: 'mailto:hello@whereat.kr',
    },
  ];

  return (
    <div className="pricing-page">
      {/* Hero Section */}
      <section className="pricing-hero">
        <div className="container">
          <h1>{isKo ? '브랜드 규모에 맞는 유연한 가격 구성' : 'Flexible pricing for every brand size'}</h1>
          {/* D-2 (2026-10-03): "연간 선불 요금제" / "Annual prepaid plans" lost
              선불 / "prepaid". The Korean states the 12-month fee charged once
              on the purchase date, as the FAQ answer below words it. Copy
              status: pending_ceo (D9) -- the plan gives the phrase, not this
              whole Korean sentence. The English keeps "billed annually", the
              wording D-2 keeps on the period lines above, the '/' cards and
              checkout (sentence given verbatim in the 2026-10-04 follow-up). */}
          <p>{isKo ? '결제일에 12개월 이용료를 한 번 결제하는 요금제입니다. 자동 갱신은 없으며, 이용하지 않은 개월은 환불됩니다.' : 'Annual plans, billed annually. No auto-renewal, and unused months are refundable.'}</p>
        </div>
      </section>

      {/* Pricing Grid */}
      <section className="pricing-section">
        <div className="container">
          <div className="pricing-grid">
            {plans.map((plan, index) => (
              <div 
                key={index} 
                className={`pricing-card ${plan.popular ? 'pricing-card-popular' : ''}`}
              >
                {plan.popular && (
                  <div className="pricing-badge">
                    <span>{plan.badge}</span>
                  </div>
                )}
                
                <div className="pricing-header">
                  <h3>{plan.name}</h3>
                  
                  {/* A1 — THE PERIOD IS DATA, NOT INFERRED FROM THE STRING.
                      This used to read:
                        {plan.price.startsWith('₩') && <span>/월</span>}
                      i.e. '/월' was appended to ANY price beginning with ₩. That
                      was survivable only while `price` happened to hold a monthly
                      figure. The moment it became the ANNUAL total, the public
                      pricing page would have rendered '₩2,388,000 /월' — a 12x
                      overquote, one click from a live card checkout, and in the
                      EN locale too since the suffix was hardcoded Korean.
                      Same class of defect as the wire-request 12x overcharge that
                      backend/app/api/profile.py:480-492 exists to warn about.
                      Each plan now states its own suffix, or omits it. */}
                  <div className="pricing-price">
                    <p className="price-main">{plan.price}</p>
                    {plan.priceSuffix && <span className="price-period">{plan.priceSuffix}</span>}
                  </div>
                  
                  {plan.priceOriginal && (
                    <>
                      <p className="price-original">{plan.priceOriginal}</p>
                      <p className="price-discount">{plan.discount}</p>
                    </>
                  )}
                  
                  <p className="price-period-text">{plan.period}</p>
                  <p className="plan-description">{plan.description}</p>
                </div>

                
                <ul className="pricing-features">
                  {plan.features.map((feature, i) => (
                    <li key={i}>
                      <span className="feature-icon">{feature.icon}</span>
                      <span>{feature.text}</span>
                    </li>
                  ))}
                </ul>
                
                <a 
                  href={plan.ctaLink}
                  className={`btn ${plan.ctaClass}`}
                  style={{ width: '100%' }}
                >
                  {plan.cta}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ.
          The early-bird answer was removed with the plan table above: it quoted
          a ₩39,000 list price and a deadline of 2025-06-30, neither of which was
          ever true of the shipped catalogue.

          The refund answer is RENDERED from constants/legal.js. It was one of
          the six divergent refund statements §6.1 catalogued; hand-writing it
          here again is how the divergence comes back. */}
      <section className="pricing-faq">
        <div className="container">
          <h2>{isKo ? '자주 묻는 가격 질문' : 'Pricing FAQ'}</h2>
          <div className="faq-grid">
            {/* 2026-10-03 (Portone-KGinisys-Pricing-fix-10-2.md T1.3): the
                three answers below asked and answered "what is a credit". Only
                the unit and the verb changed -- 분석 리포트 N건, 제공 -- and the
                billing meaning is the same. */}
            <div className="card">
              <p className="faq-question">{isKo ? '분석 리포트 1건은 어떻게 계산되나요?' : 'How is one analysis report counted?'}</p>
              {/* The cap is MAX_LENGTHS.analyze_text, the limit the Analyze box
                  enforces (it mirrors the backend PROFILES.analyze_text). It
                  moved from 3,000 to 300 on 2026-08-16; a hand-typed copy here
                  would be one more number to drift. Viewing, PDF export and
                  sharing cost nothing (api/reports.py), so the answer says so. */}
              <p className="faq-answer">
                {isKo
                  ? `마케팅 문구 1건(최대 ${MAX_LENGTHS.analyze_text}자)을 검증하면 분석 리포트 1건이 작성되며, 요금제에 포함된 건수에서 1건이 사용됩니다. 생성된 리포트의 열람·PDF 다운로드·공유는 건수에 포함되지 않습니다.`
                  : `Each marketing claim you check (up to ${MAX_LENGTHS.analyze_text} characters) produces one analysis report and uses one of the reports included in your plan. Viewing, downloading or sharing a report does not count.`}
              </p>
            </div>
            <div className="card">
              <p className="faq-question">{isKo ? '요금은 어떻게 청구되나요?' : 'How am I billed?'}</p>
              <p className="faq-answer">
                {isKo
                  ? 'Starter와 Pro는 12개월 이용 요금제입니다. 결제일에 12개월 이용료가 한 번 결제되고, 요금제에 포함된 분석 리포트는 매월 제공됩니다. 자동 갱신은 없습니다.'
                  : 'Starter and Pro are 12-month plans. The 12-month fee is charged once on the purchase date, and the analysis reports included in your plan are provided each month. There is no automatic renewal.'}
              </p>
            </div>
            <div className="card">
              <p className="faq-question">{isKo ? '이번 달에 이용하지 않은 분석 리포트는 다음 달로 이월되나요?' : 'Do unused analysis reports carry over to the next month?'}</p>
              {/* ⚠ THIS IS A TERM OF SALE, scoped to the PLAN allowance on
                  purpose. The monthly grant is a hard reset (Q3), so the reports
                  a plan provides genuinely do not carry over. What does survive
                  the reset is `organizations.credits_purchased`, a non-expiring
                  subset of the pool (T10a, 2026-09-24): the anniversary grant is
                  `credits_remaining = :allowance + LEAST(credits_purchased,
                  credits_remaining)` (plan_credit_service.py). That is why the
                  answer says "요금제에 포함된" and never makes a blanket claim.

                  The second sentence that used to follow -- that units bought or
                  granted separately never expire -- was dropped on 2026-10-03
                  (T1.3). Nothing is sold separately (packs are withdrawn), the
                  pool is zeroed when a plan ends, and a never-expires promise
                  about a unit is exactly the stored-value wording KG이니시스
                  reviews as 환금성. The few orgs holding such extras (the one
                  legacy pack holder, admin goodwill grants) are told about them
                  in the logged-in usage panel instead (OrgCreditPanel).

                  If the carry rule changes again, this answer changes with it --
                  do not restore a blanket claim. */}
              <p className="faq-answer">
                {isKo
                  ? '요금제에 포함된 분석 리포트는 매월 새로 제공되며, 그달에 이용하지 않은 건은 다음 달로 이월되지 않습니다.'
                  : 'The analysis reports included in your plan are provided anew each month, and any not used within that month do not carry over.'}
              </p>
            </div>
            <div className="card">
              <p className="faq-question">{isKo ? '환불이 가능한가요?' : 'Can I get a refund?'}</p>
              <p className="faq-answer">
                {isKo ? REFUND_POLICY_SUMMARY.ko : REFUND_POLICY_SUMMARY.en}{' '}
                <Link to="/about#refund">
                  {isKo ? '환불 및 청약철회 정책 전문 보기' : 'Read the full refund and withdrawal policy'}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default PricingPage;
